import { useQuery } from '@apollo/client';
import React, { forwardRef, useImperativeHandle } from 'react';
import { FetchTaskListQuery, TaskStatus, TaskType } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { REQUEST_TYPE } from '../../constants/filters';
import { fetchTaskList } from '../../queries/tasks';
import { TaskListTable } from '../task-list-table/task-list-table';
import { createGroupTaskTableHead, createGroupTaskTableRows } from './group-tasks-list-utils';

interface GroupTasksList {
  refetchAll?: () => void;
}

const GroupTasksList = forwardRef(({ refetchAll }: GroupTasksList, ref) => {
  const user = useApplicationContext();
  const { data, loading, error, refetch } = useQuery<FetchTaskListQuery>(fetchTaskList, {
    skip: !user?.userId,
    variables: {
      staffId: user?.userId?.split('@')?.[0],
      taskType: TaskType.MyGroupTasks,
      taskStatus: TaskStatus.Open,
    },
  });

  const userRole = React.useMemo(() => {
    return data?.taskList?.userRole;
  }, [data]);

  const head = React.useMemo(() => createGroupTaskTableHead(true), []);

  const visibleRows = React.useMemo(() => {
    const taskDetails = data?.taskList?.tasks ?? [];
    return createGroupTaskTableRows({ taskslist: taskDetails, userRole, refetchAll, refetch });
  }, [data?.taskList?.tasks, refetchAll, refetch, userRole]);

  useImperativeHandle(ref, () => ({
    reload: refetch,
  }));

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <TaskListTable
      rows={visibleRows}
      head={head}
      headingText="Group requests"
      isLoading={loading}
      requestTypeFilterOptions={REQUEST_TYPE}
    />
  );
});

export default GroupTasksList;
